// UTM + referrer capture utility.
//
// Captures attribution params on every page load. Persists them for
// the session (`sessionStorage`) so form submissions made on a later
// page still carry the original source. First-touch attribution is
// preferred: once a value is set, the new URL doesn't overwrite it.
//
// NOTE: durable cross-session attribution (`am_attribution_v1` in
// localStorage) is owned by `lib/attribution.js`, which is mounted
// alongside this module. That file is the source of truth for src,
// utm_source, utm_campaign, ref, and referrer once a visitor returns.
// We READ from it here to build a normalized {src, campaign} payload
// for Phase 2 revenue tracking, but we never write to it from this
// file to avoid schema drift.

import { getAttribution as readDurableAttribution } from "./attribution";

const SESSION_KEY = "am-utm";

const PARAM_KEYS = [
  "src",
  "campaign",
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "ref",
];

const readSession = () => {
  if (typeof window === "undefined") return {};
  try {
    return JSON.parse(window.sessionStorage.getItem(SESSION_KEY) || "{}");
  } catch {
    return {};
  }
};

const writeSession = (obj) => {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(SESSION_KEY, JSON.stringify(obj));
  } catch {
    /* swallow */
  }
};

/**
 * Capture attribution params from the current URL, persist for the
 * session, and fire a one-time visit ping to the backend.
 * Safe to call on every route change.
 */
export const captureUtm = () => {
  if (typeof window === "undefined") return {};
  const stored = readSession();
  const url = new URL(window.location.href);
  const next = { ...stored };

  PARAM_KEYS.forEach((k) => {
    const v = url.searchParams.get(k);
    if (v && !next[k]) next[k] = v;
  });

  if (!next.referrer && document.referrer && !document.referrer.includes(window.location.host)) {
    next.referrer = document.referrer;
  }
  if (!next.landing_path) {
    next.landing_path = window.location.pathname + window.location.search;
  }
  if (!next.captured_at) {
    next.captured_at = new Date().toISOString();
  }

  writeSession(next);

  // Single attribution-visit ping per browser session so the admin
  // Sources dashboard can count visits-by-channel. Analytics must
  // never block, so failures are swallowed.
  try {
    if (!window.sessionStorage.getItem("am-attribution-pinged")) {
      window.sessionStorage.setItem("am-attribution-pinged", "1");
      const apiBase = process.env.REACT_APP_BACKEND_URL;
      if (apiBase) {
        const v1 = getAttributionV2();
        fetch(`${apiBase}/api/attribution-visit`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            src: v1.src,
            campaign: v1.campaign,
            landing_path: next.landing_path || null,
            referrer: next.referrer || null,
          }),
          keepalive: true,
        }).catch(() => {});
      }
    }
  } catch {
    /* swallow */
  }

  return next;
};

/** Return the persisted UTM payload (sessionStorage). */
export const getUtm = () => readSession();

/**
 * Phase 2 attribution: returns a normalized {src, campaign} pair
 * derived from the durable `am_attribution_v1` localStorage owned by
 * lib/attribution.js, with the same fallback rules the backend uses.
 *   src      = src ?? utm_source ?? ref ?? "direct_or_unknown"
 *   campaign = campaign ?? utm_campaign ?? null
 */
export const getAttributionV2 = () => {
  const a = readDurableAttribution() || {};
  const t = a.last_touch || a.first_touch || {};
  const src = (t.src || t.utm_source || t.ref || "direct_or_unknown").toString().toLowerCase();
  const campaign = t.campaign || t.utm_campaign || null;
  return { src, campaign };
};

/**
 * Attach attribution data to a payload, namespaced under `attribution`.
 * Used by lead/feedback/contact forms. Merges sessionStorage UTM with
 * the durable {src, campaign} so backend always sees the canonical
 * source even when the visitor lost the URL params after navigation.
 */
export const withAttribution = (payload = {}) => {
  const utm = getUtm();
  const v1 = getAttributionV2();
  const merged = { ...utm, src: v1.src, campaign: v1.campaign };
  return { ...payload, attribution: merged };
};
